import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { useEffect } from 'react';
import {
    BUSTLY_HOSTED_CONNECT_RESULT_MESSAGE,
    bustlyHostedConnectFlowIdFromCallbackUrl,
    bustlyHostedConnectResultChannelName,
    readBustlyHostedConnectHandoff,
    parseBustlyHostedConnectResult,
} from '@/contexts/AuthContext/bustlyHostedConnect';

const BustlyHostedConnectCallbackPage = () => {
    const flowId = bustlyHostedConnectFlowIdFromCallbackUrl(window.location.href);
    const handoff = readBustlyHostedConnectHandoff();
    const result = parseBustlyHostedConnectResult(window.location.href, handoff);
    const isSuccessful = result?.status === 'success' || result?.status === 'pending';
    const errorMessage = result?.error || 'We could not finish this connection. Close this window and try again.';

    useEffect(() => {
        if (!result) {
            return;
        }

        const message = {
            type: BUSTLY_HOSTED_CONNECT_RESULT_MESSAGE,
            result,
            flowId,
        };
        try {
            const resultChannel = new BroadcastChannel(bustlyHostedConnectResultChannelName(flowId));
            resultChannel.postMessage(message);
            resultChannel.close();
        } catch {
            // The opener and URL polling paths remain available as fallbacks.
        }
        window.opener?.postMessage(message, window.location.origin);

        if (isSuccessful) {
            // URL polling remains the fallback when a provider strips window.opener.
            // Keep this window open long enough for the parent to consume the callback.
            const closeTimer = window.setTimeout(() => window.close(), 1000);
            return () => window.clearTimeout(closeTimer);
        }
    }, [flowId, isSuccessful, result]);

    return (
        <main className="flex min-h-screen items-center justify-center bg-white px-6 text-center">
            <div className="flex max-w-md flex-col items-center gap-4">
                {isSuccessful ? (
                    <CheckCircle size={48} weight="fill" className="text-green-600" />
                ) : (
                    <WarningCircle size={48} weight="fill" className="text-red-600" />
                )}
                <h1 className="text-2xl font-semibold text-gray-900">
                    {isSuccessful ? 'Connection complete' : 'Connection not completed'}
                </h1>
                <p className="text-base text-gray-500">
                    {isSuccessful
                        ? 'Bustly is finishing the connection. This window will close automatically.'
                        : errorMessage}
                </p>
                {!isSuccessful && (
                    <button
                        type="button"
                        onClick={() => window.close()}
                        className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white"
                    >
                        Close and try again
                    </button>
                )}
            </div>
        </main>
    );
};

export default BustlyHostedConnectCallbackPage;
